/**
 * FR-017.7 — the "Art" field both add forms share, and the payload each type
 * needs.
 *
 * By default only the types whose rule needs NO line anchor are offered:
 * `generic`, `validity`, `ownership`. `price` and `quantity` require one
 * (`libs/twin_links` TYPE_RULES) and picking a line needs the document open,
 * so only the document page's form offers them (FR-017 #5), and only when the
 * document has a row that can be anchored; the object page's form keeps the
 * default and would otherwise produce a 422 the farmer cannot fix there.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { linkTypeLabel } from '../../services/linkTypeLabels';
import { parseGermanInput } from './lineRows';

export const OFFERED_LINK_TYPES = ['generic', 'validity', 'ownership'] as const;
/** The types anchored to one extracted row (FR-017 #5). */
export const LINE_LINK_TYPES = ['price', 'quantity'] as const;

export function isLineLinkType(linkType: string): boolean {
  return (LINE_LINK_TYPES as readonly string[]).includes(linkType);
}

/**
 * A line link in the making: the picked row (`anchorRef`, `null` until one is
 * picked) and the payload values, as the farmer typed them. `direction` starts
 * empty — nothing chooses "Zugang" or "Abgang" for the farmer.
 */
export interface LineDraft {
  anchorRef: string | null;
  unitPrice: string;
  currency: string;
  quantity: string;
  unit: string;
  direction: '' | 'in' | 'out';
  observedAt: string;
}

export const EMPTY_LINE_DRAFT: LineDraft = {
  anchorRef: null,
  unitPrice: '',
  currency: '',
  quantity: '',
  unit: '',
  direction: '',
  observedAt: '',
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CURRENCY = /^[A-Z]{3}$/;

/**
 * The payload the chosen type needs. Numbers go out canonical (`"22,50"` →
 * `"22.5"`): the value is the farmer's, the spelling is the one every
 * validator reads the same way.
 */
export function linkPayload(linkType: string, validUntil: string, line?: LineDraft): Record<string, unknown> {
  if (linkType === 'validity') return { valid_until: validUntil };
  if (!line || !isLineLinkType(linkType)) return {};
  const common = {
    quantity: parseGermanInput(line.quantity) ?? line.quantity,
    unit: line.unit.trim(),
    observed_at: line.observedAt,
  };
  if (linkType === 'quantity') return { ...common, direction: line.direction };
  return {
    unit_price: parseGermanInput(line.unitPrice) ?? line.unitPrice,
    currency: line.currency.trim().toUpperCase(),
    ...common,
  };
}

/** Whether the type's required payload is filled. The backend would refuse an
 *  incomplete one; saying so before the round trip is kinder. */
export function linkTypeComplete(linkType: string, validUntil: string, line?: LineDraft): boolean {
  if (linkType === 'validity') return validUntil !== '';
  if (!isLineLinkType(linkType)) return true;
  if (!line || line.anchorRef === null) return false;
  const common =
    parseGermanInput(line.quantity) !== null && line.unit.trim() !== '' && ISO_DATE.test(line.observedAt);
  if (linkType === 'quantity') return common && (line.direction === 'in' || line.direction === 'out');
  return common && parseGermanInput(line.unitPrice) !== null && CURRENCY.test(line.currency.trim().toUpperCase());
}

interface LinkTypeFieldsProps {
  linkType: string;
  onLinkType: (value: string) => void;
  validUntil: string;
  onValidUntil: (value: string) => void;
  /** The types to offer; defaults to the anchorless ones. */
  offered?: readonly string[];
  /** The line link being drafted; its value fields show once a row is picked. */
  line?: LineDraft;
  onLine?: (next: LineDraft) => void;
  /** Rendered under "Art" for a line type — the document page's line picker. */
  children?: React.ReactNode;
}

export const LinkTypeFields: React.FC<LinkTypeFieldsProps> = ({
  linkType,
  onLinkType,
  validUntil,
  onValidUntil,
  offered = OFFERED_LINK_TYPES,
  line,
  onLine,
  children,
}) => {
  const { t } = useTranslation();
  const lineType = isLineLinkType(linkType);
  const set = (patch: Partial<LineDraft>) => line && onLine?.({ ...line, ...patch });
  return (
    <>
      <label>
        {t('twinLinks.form.linkType')}
        <select value={linkType} onChange={(e) => onLinkType(e.target.value)}>
          {offered.map((value) => (
            <option key={value} value={value}>
              {linkTypeLabel(value)}
            </option>
          ))}
        </select>
      </label>
      {linkType === 'validity' && (
        <label>
          {t('twinLinks.form.validUntil')}
          <input type="date" value={validUntil} onChange={(e) => onValidUntil(e.target.value)} />
        </label>
      )}
      {lineType && children}
      {lineType && line && line.anchorRef !== null && (
        <>
          {linkType === 'price' && (
            <>
              <label>
                {t('twinLinks.form.unitPrice')}
                <input
                  type="text"
                  inputMode="decimal"
                  value={line.unitPrice}
                  onChange={(e) => set({ unitPrice: e.target.value })}
                />
              </label>
              <label>
                {t('twinLinks.form.currency')}
                <input type="text" value={line.currency} onChange={(e) => set({ currency: e.target.value })} />
              </label>
            </>
          )}
          <label>
            {t('twinLinks.form.quantity')}
            <input
              type="text"
              inputMode="decimal"
              value={line.quantity}
              onChange={(e) => set({ quantity: e.target.value })}
            />
          </label>
          <label>
            {t('twinLinks.form.unit')}
            <input type="text" value={line.unit} onChange={(e) => set({ unit: e.target.value })} />
          </label>
          {linkType === 'quantity' && (
            // No default: a delivery note reads as stock IN for the buyer and
            // OUT for the seller, and the document does not say which side the
            // farmer is on. A silent default would be wrong half the time and
            // nothing downstream could tell.
            <fieldset role="radiogroup" aria-label={t('twinLinks.form.direction')}>
              <legend>{t('twinLinks.form.direction')}</legend>
              <label>
                <input
                  type="radio"
                  name="line-link-direction"
                  value="in"
                  checked={line.direction === 'in'}
                  onChange={() => set({ direction: 'in' })}
                />
                {t('twinLinks.details.in')}
              </label>
              <label>
                <input
                  type="radio"
                  name="line-link-direction"
                  value="out"
                  checked={line.direction === 'out'}
                  onChange={() => set({ direction: 'out' })}
                />
                {t('twinLinks.details.out')}
              </label>
            </fieldset>
          )}
          <label>
            {t('twinLinks.form.observedAt')}
            <input type="date" value={line.observedAt} onChange={(e) => set({ observedAt: e.target.value })} />
          </label>
        </>
      )}
    </>
  );
};

export default LinkTypeFields;
